import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import Breadcrumbs from "../../../../components/BreadCrumbs";
import {
  UIPageFormFooter,
  UIPhoneField,
  UISelect,
  UITextField,
  UIUpload,
} from "../../../../components/ui";
import { uploadFileWithProgress } from "../../../../utils/Helpers";
import { getCompanyRolesRequest } from "../../../../store/CompanyPortal/Roles/actions";
import { createCompanyUserRequest } from "../../../../store/CompanyPortal/Employees/actions";
import { splitPhoneForApi } from "../employeeMappers";
import { companyPermissionModules } from "../../../../utils/Constants";
import { canViewCompanyModules } from "../../../../utils/Permissions";

import "./AddEmployee.scss";

const EMPLOYEES_TAB_PATH = `${ROUTE_PATHS.companyTeamsPermissions}?tab=employees`;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const AddEmployee = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const creating = useSelector((state) => state.companyEmployees.creating);
  const roleOptions = useSelector((state) => state.companyRoles.list);
  const canViewRoles = canViewCompanyModules(
    companyPermissionModules.rolesManagement,
    login,
  );
  const teamCopy = messages[lang].teamManagement;
  const copy = teamCopy.addEmployee;

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  // Full international phone digits (incl. dial code); the dial code itself is
  // tracked separately (digits only, no +) from PhoneInput's countryData.
  const [phone, setPhone] = useState("");
  const [phoneCountryDialCode, setPhoneCountryDialCode] = useState("966");
  const [role, setRole] = useState(null);
  const [pictureFile, setPictureFile] = useState(null);
  const [picturePreview, setPicturePreview] = useState(null);
  const [uploadState, setUploadState] = useState({ target: null, percent: 0 });
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState({});

  // GET /company_roles requires Roles Management view — same guard as ViewEmployee.
  useEffect(() => {
    if (!canViewRoles) return;
    dispatch(getCompanyRolesRequest({ page: 1, items: 100 }));
  }, [dispatch, canViewRoles]);

  const trimmedName = name.trim();
  const trimmedEmail = email.trim();

  const errors = useMemo(() => {
    const next = {};
    if (!trimmedName) next.name = copy.validations.nameRequired;
    if (!trimmedEmail) next.email = copy.validations.emailRequired;
    else if (!EMAIL_REGEX.test(trimmedEmail))
      next.email = copy.validations.emailInvalid;
    if (!role) next.role = copy.validations.roleRequired;
    return next;
  }, [trimmedName, trimmedEmail, role, copy.validations]);

  const showError = (field) =>
    Boolean((submitted || touched[field]) && errors[field]);

  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const uploadingPicture = uploadState.target === "profile_picture";
  const busy = creating || uploadingPicture;

  const goBackToList = () => history.push(EMPLOYEES_TAB_PATH);

  const handleSubmit = async () => {
    setSubmitted(true);
    if (Object.keys(errors).length > 0 || busy) return;

    let profilePictureUrl;
    if (pictureFile) {
      try {
        profilePictureUrl = await uploadFileWithProgress(
          pictureFile,
          "profile_picture",
          "employee",
          setUploadState,
        );
      } finally {
        setUploadState({ target: null, percent: 0 });
      }
      if (!profilePictureUrl) return;
    }

    const data = {
      name: trimmedName,
      email: trimmedEmail,
      company_role_id: Number(role.id),
    };
    // Mobile is optional, but when entered the backend needs BOTH the national
    // number and the dial code to validate/resolve the country.
    const { dial_code, phone_number } = splitPhoneForApi(
      phone,
      phoneCountryDialCode,
    );
    if (phone_number) {
      data.phone_number = phone_number;
      data.dial_code = dial_code;
    }
    if (profilePictureUrl) {
      data.profile_picture_url = profilePictureUrl;
    }

    dispatch(
      createCompanyUserRequest({
        data,
        successMessage: copy.createSuccess,
        onSuccess: goBackToList,
      }),
    );
  };

  const breadcrumbList = [
    { path: ROUTE_PATHS.companyTeamsPermissions, label: teamCopy.title },
    { path: EMPLOYEES_TAB_PATH, label: teamCopy.tabs.employees },
    { path: "", label: copy.title },
  ];

  return (
    <div className="team-management add-employee">
      <div className="mt-2">
        <Breadcrumbs list={breadcrumbList} BreadCrumbHasBackIcon={false} />
      </div>

      <div className="add-employee__card">
        <div className="add-employee__card-header">{copy.detailsTitle}</div>

        <div className="add-employee__card-body">
          <div className="add-employee__avatar-col">
            <UIUpload
              name="profilePicture"
              className="add-employee__avatar"
              variant="logo"
              label={copy.pictureLabel}
              uploadText={copy.uploadPicture}
              changeText={copy.changePicture}
              preview={picturePreview || ""}
              file={pictureFile}
              showRemove
              isUploading={uploadingPicture}
              uploadProgress={uploadingPicture ? uploadState.percent : undefined}
              uploadProgressLabel={
                uploadingPicture
                  ? `${copy.uploading} · ${uploadState.percent}%`
                  : null
              }
              onChange={(file, preview) => {
                if (!file) {
                  setPictureFile(null);
                  setPicturePreview(null);
                  return;
                }
                setPictureFile(file);
                setPicturePreview(typeof preview === "string" ? preview : null);
              }}
              disabled={busy}
            />
          </div>

          <div className="add-employee__grid">
            <UITextField
              label={copy.fields.name}
              name="name"
              required
              value={name}
              placeholder={copy.fields.namePlaceholder}
              onChange={(event) => setName(event.target.value)}
              onBlur={() => markTouched("name")}
              error={showError("name")}
              helperText={showError("name") ? errors.name : null}
            />
            <UITextField
              label={copy.fields.email}
              name="email"
              required
              value={email}
              placeholder={copy.fields.emailPlaceholder}
              onChange={(event) => setEmail(event.target.value)}
              onBlur={() => markTouched("email")}
              error={showError("email")}
              helperText={showError("email") ? errors.email : null}
            />
            <UIPhoneField
              label={copy.fields.mobileNumber}
              name="mobileNumber"
              country="sa"
              preferredCountries={["eg", "sa", "ae"]}
              value={phone}
              formValueIsFullInternational
              onChange={(fullValue, countryData) => {
                setPhone(fullValue);
                const dc = countryData?.dialCode;
                if (dc != null && dc !== "") {
                  setPhoneCountryDialCode(String(dc).replace(/\D/g, ""));
                }
              }}
            />
            <UISelect
              label={copy.fields.role}
              name="role"
              required
              placeholder={copy.fields.rolePlaceholder}
              options={roleOptions}
              value={role}
              onChange={(nextValue) => setRole(nextValue)}
              onBlur={() => markTouched("role")}
              error={showError("role")}
              helperText={showError("role") ? errors.role : null}
            />
          </div>
        </div>
      </div>

      <UIPageFormFooter
        cancelLabel={copy.cancel}
        submitLabel={copy.submit}
        onCancel={goBackToList}
        onSubmit={handleSubmit}
        cancelDisabled={busy}
        submitDisabled={busy || Object.keys(errors).length > 0}
        submitLoading={busy}
      />
    </div>
  );
};

export default AddEmployee;
